import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, FileText, List, Upload, GitBranch, Network, Download, Edit3, X, Info } from 'lucide-react';
import MindmapView from './MindmapView';

interface Node {
  id: string;
  label: string;
  level: number;
  children: Node[];
  content: string;
}

interface ParsedMarkdown {
  nodes: Node[];
  flatNodes: Node[];
}

const getLineDifferences = (original: string, current: string): {
  modified: Set<number>,
  added: Set<number>,
  deleted: Array<{ originalLine: number, content: string }>
} => {
  const originalLines = original ? original.split('\n') : [];
  const currentLines = current ? current.split('\n') : [];
  const addedLines = new Set<number>();
  const deletedLines: Array<{ originalLine: number, content: string }> = [];

  const m = originalLines.length;
  const n = currentLines.length;

  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = originalLines[i - 1] === currentLines[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && originalLines[i - 1] === currentLines[j - 1]) {
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      addedLines.add(j - 1);
      j--;
    } else {
      deletedLines.push({ originalLine: i - 1, content: originalLines[i - 1] });
      i--;
    }
  }

  return { modified: new Set<number>(), added: addedLines, deleted: deletedLines };
};

// Enhanced CodeMirror-like editor component with dot-only change tracking
const CodeEditor: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  originalValue: string;
}> = ({ value, onChange, placeholder, originalValue }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [lineCount, setLineCount] = useState(1);
  const lineDifferences = getLineDifferences(originalValue || '', value || '');
  const hasChanges = lineDifferences.modified.size > 0 || lineDifferences.added.size > 0 || lineDifferences.deleted.length > 0;
  const totalModified = lineDifferences.modified.size;
  const totalAdded = lineDifferences.added.size;
  const totalDeleted = lineDifferences.deleted.length;

  useEffect(() => {
    try {
      const lines = (value || '').split('\n').length;
      setLineCount(Math.max(1, lines));
    } catch (error) {
      console.error('Error calculating line count:', error);
      setLineCount(1);
    }
  }, [value]);

  useEffect(() => {
    const focusTimer = setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);
    return () => clearTimeout(focusTimer);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = e.target as HTMLTextAreaElement;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      
      if (e.shiftKey) {
        // Shift+Tab: Remove indent
        const beforeCursor = value.substring(0, start);
        const afterCursor = value.substring(end);
        const lines = beforeCursor.split('\n');
        const currentLineIndex = lines.length - 1;
        const currentLine = lines[currentLineIndex];
        
        let newCursorPos = start;
        let newValue = value;
        
        if (currentLine.startsWith('    ')) {
          // Remove 4 spaces
          lines[currentLineIndex] = currentLine.substring(4);
          newValue = lines.join('\n') + afterCursor;
          newCursorPos = start - 4;
        } else if (currentLine.startsWith('\t')) {
          // Remove tab
          lines[currentLineIndex] = currentLine.substring(1);
          newValue = lines.join('\n') + afterCursor;
          newCursorPos = start - 1;
        }
        
        onChange(newValue);
        
        // Use requestAnimationFrame to ensure cursor position is set after DOM update
        requestAnimationFrame(() => {
          if (textarea && newCursorPos >= 0) {
            textarea.focus();
            textarea.setSelectionRange(newCursorPos, newCursorPos);
          }
        });
        
      } else {
        // Tab: Add indent (4 spaces)
        const newValue = value.substring(0, start) + '    ' + value.substring(end);
        const newCursorPos = start + 4;
        
        onChange(newValue);
        
        // Use requestAnimationFrame to ensure cursor position is set after DOM update
        requestAnimationFrame(() => {
          if (textarea) {
            textarea.focus();
            textarea.setSelectionRange(newCursorPos, newCursorPos);
          }
        });
      }
    }
  }, [value, onChange]);

  return (
    <div className="flex h-full border border-gray-300 rounded-lg overflow-hidden bg-white">
      {/* Line numbers with change indicator dots only */}
      <div className="bg-gray-50 border-r border-gray-200 px-3 py-4 text-sm font-mono select-none min-w-[3rem] text-right relative">
        {Array.from({ length: Math.max(lineCount, (originalValue || '').split('\n').length) }, (_, i) => {
          const isCurrentLine = i < lineCount;
          const isDeletedLine = !isCurrentLine && i < (originalValue || '').split('\n').length;
          
          return (
            <div key={i + 1} className={`leading-6 relative ${
              isDeletedLine ? 'text-red-700 font-semibold' :
              lineDifferences.modified.has(i) ? 'text-amber-700 font-semibold' : 
              lineDifferences.added.has(i) ? 'text-green-700 font-semibold' : 'text-gray-500'
            }`}>
              {/* Change indicator dots */}
              {isDeletedLine && (
                <div className="absolute -left-1 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-red-500 rounded-full"></div>
              )}
              {lineDifferences.modified.has(i) && (
                <div className="absolute -left-1 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-amber-500 rounded-full"></div>
              )}
              {lineDifferences.added.has(i) && (
                <div className="absolute -left-1 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-green-500 rounded-full"></div>
              )}
              {isDeletedLine ? '∅' : i + 1}
            </div>
          );
        })}
      </div>
      
      {/* Clean editor without line highlighting */}
      <div className="flex-1 relative">
        {/* Actual textarea */}
        <textarea
          ref={textareaRef}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full h-full resize-none p-4 font-mono text-sm focus:outline-none bg-white leading-6 relative z-20"
          placeholder={placeholder}
          spellCheck={false}
          style={{ minHeight: '50vh' }}
        />
        
        {/* Change indicator overlay */}
        {hasChanges && (
          <div className="absolute top-2 right-2 flex items-center space-x-2 bg-white border border-gray-300 rounded-lg px-3 py-1 shadow-sm z-30">
            <div className="flex items-center space-x-1">
              {totalModified > 0 && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="text-xs text-amber-700">{totalModified}</span>
                </div>
              )}
              {totalAdded > 0 && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-xs text-green-700">{totalAdded}</span>
                </div>
              )}
              {totalDeleted > 0 && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-xs text-red-700">{totalDeleted}</span>
                </div>
              )}
            </div>
            <span className="text-xs font-medium text-gray-700">
              {totalModified + totalAdded + totalDeleted} change{totalModified + totalAdded + totalDeleted !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const MarkdownVisualizer: React.FC = () => {
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [originalMarkdownContent, setOriginalMarkdownContent] = useState<string>('');
  const [parsedData, setParsedData] = useState<ParsedMarkdown | null>(null);
  const [currentView, setCurrentView] = useState<'accordion' | 'flow' | 'mindmap' | 'list' | 'edit'>('accordion');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [editContent, setEditContent] = useState<string>('');
  const [fileName, setFileName] = useState<string>('mindmap.md');
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [isAutoSaving, setIsAutoSaving] = useState<boolean>(false);
  const [lastAutoSaved, setLastAutoSaved] = useState<Date | null>(null);
  const [showAutoSaveStatus, setShowAutoSaveStatus] = useState<boolean>(false);
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const parseMarkdown = useCallback((content: string): ParsedMarkdown => {
    const lines = content.split('\n');
    const nodes: Node[] = [];
    const flatNodes: Node[] = [];
    const stack: Node[] = [];

    lines.forEach((line, index) => {
      if (!line.trim()) return;

      let level = 1;
      let label = '';

      if (line.startsWith('#')) {
        const headerMatch = line.match(/^(#+)\s*(.+)$/);
        if (headerMatch) {
          level = headerMatch[1].length;
          label = headerMatch[2].trim();
        }
      } else if (line.match(/^(\s*)([-*+]|\d+\.)\s+/)) {
        const listMatch = line.match(/^(\s*)([-*+]|\d+\.)\s+(.+)$/);
        if (listMatch) {
          const indentString = listMatch[1];
          const tabCount = (indentString.match(/\t/g) || []).length;
          const spaceCount = (indentString.match(/ /g) || []).length;
          const indentLevel = tabCount + Math.floor(spaceCount / 4);
          
          if (indentLevel === 0 && stack.length > 0) {
            let headerParent = null;
            for (let i = stack.length - 1; i >= 0; i--) {
              if (stack[i].content.startsWith('#')) {
                headerParent = stack[i];
                break;
              }
            }
            if (headerParent) {
              level = headerParent.level + 1;
            } else {
              level = 1;
            }
          } else {
            let baseLevel = 1;
            for (let i = stack.length - 1; i >= 0; i--) {
              if (stack[i].content.startsWith('#')) {
                baseLevel = stack[i].level + 1;
                break;
              }
            }
            level = baseLevel + indentLevel;
          }
          
          label = listMatch[3].trim();
        }
      } else if (line.match(/^\s/)) {
        const indentMatch = line.match(/^(\s*)(.+)$/);
        if (indentMatch) {
          const indentString = indentMatch[1];
          const tabCount = (indentString.match(/\t/g) || []).length;
          const spaceCount = (indentString.match(/ /g) || []).length;
          const indentLevel = tabCount + Math.floor(spaceCount / 4);
          
          let baseLevel = 1;
          for (let i = stack.length - 1; i >= 0; i--) {
            if (stack[i].content.startsWith('#')) {
              baseLevel = stack[i].level + 1;
              break;
            }
          }
          level = baseLevel + indentLevel;
          label = indentMatch[2].trim();
        }
      } else {
        level = 1;
        label = line.trim();
      }

      if (!label) return;

      const node: Node = {
        id: `node-${index}`,
        label,
        level,
        children: [],
        content: line
      };

      while (stack.length > 0 && stack[stack.length - 1].level >= level) {
        stack.pop();
      }

      if (stack.length === 0) {
        nodes.push(node);
      } else {
        const parent = stack[stack.length - 1];
        parent.children.push(node);
      }

      stack.push(node);
      flatNodes.push(node);
    });

    return { nodes, flatNodes };
  }, []);

  const formatMarkdown = useCallback((content: string): string => {
    const lines = content.split('\n');
    const formattedLines: string[] = [];
    
    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed) {
        formattedLines.push('');
        return;
      }
      
      if (trimmed.startsWith('#')) {
        const headerMatch = trimmed.match(/^(#+)\s*(.+)$/);
        if (headerMatch) {
          formattedLines.push(`${headerMatch[1]} ${headerMatch[2]}`);
        } else {
          formattedLines.push(trimmed);
        }
        return;
      }
      
      if (trimmed.match(/^[-*+]\s+/) || trimmed.match(/^\d+\.\s+/)) {
        const leadingWhitespace = line.match(/^(\s*)/)?.[1] || '';
        const listMatch = trimmed.match(/^([-*+]|\d+\.)\s+(.+)$/);
        if (listMatch) {
          const bullet = listMatch[1];
          formattedLines.push(`${leadingWhitespace}${bullet} ${listMatch[2]}`);
        } else {
          formattedLines.push(line);
        }
        return;
      }
      
      formattedLines.push(line);
    });
    
    return formattedLines.join('\n');
  }, []);

  const handleEditContentChange = useCallback((newContent: string) => {
    setEditContent(newContent);
    setShowAutoSaveStatus(false);
    setIsAutoSaving(false);

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      setIsAutoSaving(true);

      if (newContent && newContent !== markdownContent) {
        const formatted = formatMarkdown(newContent);
        setMarkdownContent(formatted);
        setParsedData(parseMarkdown(formatted));
      }

      setIsAutoSaving(false);
      setLastAutoSaved(new Date());
      setShowAutoSaveStatus(true);
    }, 1500);
  }, [markdownContent, formatMarkdown, parseMarkdown]);

  const processFile = useCallback((file: File, onComplete?: () => void) => {
    if (!file.name.endsWith('.md')) {
      alert('Please upload a .md file');
      return;
    }

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      const formatted = formatMarkdown(content);
      setMarkdownContent(formatted);
      setOriginalMarkdownContent(formatted);
      setEditContent(formatted);
      const parsed = parseMarkdown(formatted);
      setParsedData(parsed);
      onComplete?.();
    };
    reader.readAsText(file);
  }, [formatMarkdown, parseMarkdown]);

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    processFile(file);
  }, [processFile]);

  const handleNewCanvas = useCallback(async () => {
    const response = await fetch('/sample.md');
    const text = await response.text();
    const file = new File([text], 'sample.md', { type: 'text/markdown' });
    processFile(file, () => setCurrentView('edit'));
  }, [processFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const items = Array.from(e.dataTransfer.items);
    const hasMdFile = items.some(item => {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        return file && file.name.endsWith('.md');
      }
      return false;
    });
    
    if (hasMdFile) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const mdFiles = files.filter(file => file.name.endsWith('.md'));
    
    if (mdFiles.length === 0) {
      alert('Please drop a .md file');
      return;
    }
    
    if (mdFiles.length > 0) {
      processFile(mdFiles[0]);
    }
  }, [processFile]);

  const startEditing = useCallback(() => {
    const content = markdownContent || '';
    setEditContent(content);
    // Don't reset original content when starting to edit
  }, [markdownContent]);

  const cancelEdits = useCallback(() => {
    setEditContent(originalMarkdownContent || '');
    setMarkdownContent(originalMarkdownContent || '');
    const parsed = parseMarkdown(originalMarkdownContent || '');
    setParsedData(parsed);
    setShowAutoSaveStatus(false);
    setIsAutoSaving(false);
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
  }, [originalMarkdownContent, parseMarkdown]);

  const downloadMarkdown = useCallback(() => {
    const content = markdownContent || editContent || '';
    if (!content) return;
    
    const dataBlob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'markdown-file.md';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [markdownContent, editContent, fileName]);

  const toggleNode = useCallback((nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  const renderAccordionNode = (node: Node, depth: number = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children.length > 0;

    return (
      <div key={node.id} className={`${depth === 0 ? 'mb-3' : 'mb-2'}`}>
        <div className={`border ${depth === 0 ? 'border-gray-300' : 'border-gray-200'} rounded-lg overflow-hidden ${depth === 0 ? 'shadow-sm' : ''}`}>
          <div
            className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between ${
              hasChildren ? '' : 'cursor-default'
            }`}
            onClick={() => hasChildren && toggleNode(node.id)}
            style={{ 
              backgroundColor: depth === 0 ? 'white' : depth === 1 ? '#f8fafc' : '#f1f5f9',
              borderLeft: depth > 0 ? `3px solid ${depth === 1 ? '#3b82f6' : '#64748b'}` : 'none'
            }}
          >
            <div className="flex items-center space-x-3">
              {hasChildren ? (
                isExpanded ? (
                  <ChevronDown size={18} className="text-gray-600" />
                ) : (
                  <ChevronRight size={18} className="text-gray-600" />
                )
              ) : (
                <div className="w-4 h-4 flex items-center justify-center">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                </div>
              )}
              <span className={`font-medium ${
                depth === 0 ? 'text-lg text-gray-900' : 
                depth === 1 ? 'text-base text-gray-800' : 
                'text-sm text-gray-700'
              }`}>
                {node.label}
              </span>
            </div>
            {hasChildren && (
              <span className={`text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full ${
                depth === 0 ? 'text-sm' : 'text-xs'
              }`}>
                {node.children.length} {node.children.length === 1 ? 'item' : 'items'}
              </span>
            )}
          </div>
          
          {hasChildren && isExpanded && (
            <div className={`border-t ${depth === 0 ? 'border-gray-200' : 'border-gray-100'}`}>
              <div className="p-4 bg-gray-50">
                {node.children.map(child => renderAccordionNode(child, depth + 1))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAccordionView = () => {
    if (!parsedData) return <div className="text-center text-gray-500 py-8">No data available</div>;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Accordion View</h2>
            <p className="text-gray-600 mt-1">Explore your markdown in a hierarchical structure</p>
          </div>
          {(() => {
            const anyExpanded = parsedData.flatNodes.some(n => expandedNodes.has(n.id));
            return (
              <button
                onClick={() => anyExpanded
                  ? setExpandedNodes(prev => { const next = new Set(prev); parsedData.flatNodes.forEach(n => next.delete(n.id)); return next; })
                  : setExpandedNodes(new Set(parsedData.flatNodes.map(n => n.id)))}
                className={`flex items-center justify-center space-x-2 text-white px-4 py-2 h-10 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg font-medium min-w-[120px] ${anyExpanded ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'}`}
              >
                {anyExpanded ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                <span>{anyExpanded ? 'Collapse All' : 'Expand All'}</span>
              </button>
            );
          })()}
        </div>
        <div className="space-y-4">
          {parsedData.nodes.map(node => renderAccordionNode(node, 0))}
        </div>
      </div>
    );
  };

  const getAllFlows = useCallback((nodes: Node[]): string[][] => {
    const flows: string[][] = [];
    
    const traverse = (node: Node, currentPath: string[]) => {
      const newPath = [...currentPath, node.label];
      
      if (node.children.length === 0) {
        flows.push(newPath);
      } else {
        node.children.forEach(child => traverse(child, newPath));
      }
    };
    
    nodes.forEach(node => traverse(node, []));
    return flows;
  }, []);

  const renderFlowCard = (flow: string[], index: number) => {
    const flowId = `flow-${index}`;
    const isExpanded = expandedNodes.has(flowId);
    const leafNode = flow[flow.length - 1];
    const flowWithoutRoot = flow.slice(1);
    const firstNode = flowWithoutRoot[0];

    return (
      <div key={flowId} className="border border-gray-200 rounded-lg mb-3 shadow-sm">
        <div
          className="p-4 cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between"
          onClick={() => toggleNode(flowId)}
        >
          <div className="flex items-center space-x-3">
            {isExpanded ? (
              <ChevronDown size={18} className="text-gray-600" />
            ) : (
              <ChevronRight size={18} className="text-gray-600" />
            )}
            <div className="flex items-center space-x-2">
              <span className="font-semibold text-gray-900">Flow {index + 1}</span>
              <span className="text-sm text-gray-500">
                ({flowWithoutRoot.length} steps)
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <span>{firstNode}</span>
            <span>→</span>
            <span>...</span>
            <span>→</span>
            <span className="font-medium text-gray-700">{leafNode}</span>
          </div>
        </div>
        
        {isExpanded && (
          <div className="border-t border-gray-200 bg-gray-50 p-4">
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Complete Flow Path:</h4>
              <div className="flex flex-col space-y-3">
                {flowWithoutRoot.map((step, stepIndex) => (
                  <div key={stepIndex} className="flex items-center space-x-3">
                    <div className="flex items-center space-x-3">
                      <div 
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          stepIndex === 0 ? 'bg-blue-100 text-blue-700' :
                          stepIndex === flowWithoutRoot.length - 1 ? 'bg-red-100 text-red-700' : 
                          'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {stepIndex + 1}
                      </div>
                      <span className={`${
                        stepIndex === 0 ? 'font-semibold text-blue-900' :
                        stepIndex === flowWithoutRoot.length - 1 ? 'font-semibold text-red-900' : 
                        'font-medium text-gray-700'
                      }`}>
                        {step}
                      </span>
                      {stepIndex === 0 && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          Start
                        </span>
                      )}
                      {stepIndex === flowWithoutRoot.length - 1 && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                          End
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderFlowView = () => {
    if (!parsedData) return <div className="text-center text-gray-500 py-8">No data available</div>;

    const flows = getAllFlows(parsedData.nodes);

    const exportFlowsToJSON = () => {
      if (!parsedData || flows.length === 0) return;
      
      const flowsData = flows.map((flow, index) => ({
        flowId: index + 1,
        flowName: `Flow ${index + 1}`,
        steps: flow.slice(1),
        stepCount: flow.slice(1).length,
        completePath: flow.slice(1)
      }));

      const exportData = {
        timestamp: new Date().toISOString(),
        source: 'Markdown Visualizer and Editor - Flow View',
        totalFlows: flows.length,
        flows: flowsData
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'flows-export.json';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    return (
      <div className="space-y-6">
        <div className="mb-6">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Flow View</h2>
            <p className="text-gray-600 mt-1">{flows.length} complete flows generated from root to leaf nodes</p>
          </div>
        </div>
        
        <div className="flex items-center justify-center space-x-4 mb-6">
          {(() => {
            const anyExpanded = flows.some((_, i) => expandedNodes.has(`flow-${i}`));
            return (
              <button
                onClick={() => anyExpanded
                  ? setExpandedNodes(new Set())
                  : setExpandedNodes(new Set(flows.map((_, i) => `flow-${i}`)))}
                className={`flex items-center justify-center space-x-2 text-white px-4 py-2 h-10 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg font-medium min-w-[120px] ${anyExpanded ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'}`}
              >
                {anyExpanded ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                <span>{anyExpanded ? 'Collapse All' : 'Expand All'}</span>
              </button>
            );
          })()}
          <button
            onClick={exportFlowsToJSON}
            className="flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-4 py-2 h-10 rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-md hover:shadow-lg font-medium min-w-[120px]"
          >
            <Download size={16} />
            <span>Export JSON</span>
          </button>
        </div>
        
        <div className="space-y-4">
          {flows.map((flow, index) => renderFlowCard(flow, index))}
        </div>
      </div>
    );
  };

  const renderListView = () => {
    if (!parsedData) return <div className="text-center text-gray-500 py-8">No data available</div>;

    const labelCounts = parsedData.flatNodes.reduce((acc, node) => {
      acc[node.label] = (acc[node.label] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const collectAllItems = (node: Node): string[] => {
      const items = [node.label];
      node.children.forEach(child => items.push(...collectAllItems(child)));
      return items;
    };

    const buildHeaderStructure = () => {
      const structure: any = {};

      const processNode = (node: Node, parentKey?: string): any => {
        if (!node.content.startsWith('#')) return;
        const allItems: string[] = [];
        const subHeaders: any = {};

        node.children.forEach(child => {
          if (child.content.startsWith('#')) {
            subHeaders[child.label] = processNode(child, node.label);
          } else {
            allItems.push(...collectAllItems(child));
          }
        });

        const headerData = { level: node.level, items: allItems, subHeaders };
        if (parentKey) return headerData;
        structure[node.label] = headerData;
      };

      parsedData.nodes.forEach(node => processNode(node));
      return structure;
    };

    const headerStructure = buildHeaderStructure();

    // All unique list-item labels across every header, merged and deduplicated
    const allUniqueItems: string[] = Array.from(new Set(
      parsedData.flatNodes
        .filter(n => !n.content.startsWith('#'))
        .map(n => n.label)
    )).sort();

    const cleanStructure = (data: any): any => {
      const cleaned: any = {};
      Object.entries(data).forEach(([key, value]: [string, any]) => {
        const uniqueItems = Array.from(new Set(value.items as string[]));
        cleaned[key] = {
          level: value.level,
          items: uniqueItems.sort(),
          itemCount: uniqueItems.length,
          subHeaders: Object.keys(value.subHeaders).length > 0 ? cleanStructure(value.subHeaders) : {}
        };
      });
      return cleaned;
    };

    const exportToJSON = () => {
      const exportData = {
        timestamp: new Date().toISOString(),
        source: 'Markdown Visualizer and Editor - List View',
        totalUniqueLabels: allUniqueItems.length,
        allUniqueNodes: allUniqueItems,
        structure: cleanStructure(headerStructure)
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'list-structure.json';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    const renderHeaderSection = (headerName: string, data: any, level: number = 0) => {
      const uniqueItems: string[] = Array.from(new Set(data.items as string[])).sort();
      const hasSubHeaders = Object.keys(data.subHeaders).length > 0;
      const totalItems = uniqueItems.length + Object.keys(data.subHeaders).length;
      
      if (level >= 2) return null;
      
      return (
        <div key={headerName} className={`space-y-4 ${level > 0 ? 'ml-8' : ''}`}>
          <h3 className={`font-bold border-b-2 border-gray-200 pb-3 ${
            level === 0 ? 'text-xl text-gray-800' : 'text-lg text-gray-800'
          }`}>
            <span>
              {headerName}
            </span>
            <span className="ml-3 text-sm font-normal text-gray-500">
              ({totalItems} items)
            </span>
          </h3>
          
          {hasSubHeaders && (
            <div className="space-y-4">
              {Object.entries(data.subHeaders).map(([subHeaderName, subHeaderData]) => 
                renderHeaderSection(subHeaderName, subHeaderData, level + 1)
              )}
            </div>
          )}
          
          {uniqueItems.length > 0 && (
            <div className="grid gap-3 ml-6">
              {uniqueItems.map((item: string, index: number) => (
                <div
                  key={index}
                  className="group p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 hover:from-blue-50 hover:to-purple-50 hover:border-blue-200 transition-all duration-200 hover:shadow-md"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-800 group-hover:text-blue-700 transition-colors">
                      {item}
                    </span>
                    <span className="text-xs text-gray-500 bg-white px-3 py-1 rounded-full shadow-sm group-hover:bg-blue-100 group-hover:text-blue-700 transition-all">
                      {labelCounts[item]} {labelCounts[item] === 1 ? 'node' : 'nodes'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-6">
        <div className="mb-6">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Unique Node List</h2>
            <p className="text-gray-600 mt-1">All unique node labels across all headers, followed by nodes grouped by header</p>
          </div>
        </div>
        
        <div className="flex items-center justify-center mb-6">
          <button
            onClick={exportToJSON}
            className="flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-4 py-2 h-10 rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-md hover:shadow-lg font-medium min-w-[120px]"
          >
            <Download size={16} />
            <span>Export JSON</span>
          </button>
        </div>
        
        {/* Merged section — all unique nodes across all headers */}
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-gray-800 border-b-2 border-gray-200 pb-3">
            All Headers
            <span className="ml-3 text-sm font-normal text-gray-500">({allUniqueItems.length} items)</span>
          </h3>
          <div className="grid gap-3 ml-6">
            {allUniqueItems.map((item, index) => (
              <div
                key={index}
                className="group p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 hover:from-blue-50 hover:to-purple-50 hover:border-blue-200 transition-all duration-200 hover:shadow-md"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-800 group-hover:text-blue-700 transition-colors">
                    {item}
                  </span>
                  <span className="text-xs text-gray-500 bg-white px-3 py-1 rounded-full shadow-sm group-hover:bg-blue-100 group-hover:text-blue-700 transition-all">
                    {labelCounts[item]} {labelCounts[item] === 1 ? 'node' : 'nodes'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Per-header sections */}
        <div className="space-y-6">
          {Object.entries(headerStructure).map(([headerName, data]) =>
            renderHeaderSection(headerName, data, 0)
          )}
        </div>
      </div>
    );
  };

  const renderEditView = () => {
    const lineDifferences = getLineDifferences(originalMarkdownContent || '', editContent || '');
    const hasChanges = lineDifferences.modified.size > 0 || lineDifferences.added.size > 0 || lineDifferences.deleted.length > 0;
    const totalModified = lineDifferences.modified.size;
    const totalAdded = lineDifferences.added.size;
    const totalDeleted = lineDifferences.deleted.length;

    return (
      <div className="h-full flex flex-col" style={{ minHeight: '75vh' }}>
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Edit Markdown</h2>
            <div className="flex items-center space-x-3">
              <div className="relative group">
                <Info size={16} className="text-gray-400 hover:text-gray-600 cursor-help" />
                <div className="absolute top-full right-0 mt-2 w-80 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                  <div className="space-y-1">
                    <div className="font-medium mb-2">Enhanced Code Editor:</div>
                    <div>• Line numbers for better navigation</div>
                    <div>• Dot indicators for changes (🟢 added, 🔴 deleted)</div>
                    <div>• Auto-formats markdown on save</div>
                    <div>• Auto-saves after 1.5 seconds of inactivity</div>
                    <div>• Tab / Shift+Tab for indent/unindent</div>
                    <div>• Native undo/redo (Ctrl+Z / Ctrl+Y)</div>
                  </div>
                  <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
                </div>
              </div>
              <span className="text-xs">Hover for features</span>
            </div>
          </div>
          <p className="text-gray-600 mb-4">Professional editor with line-by-line change tracking - changes will reflect in all views</p>
          <div className="flex items-center justify-center space-x-2 mb-6">
            <button
              onClick={downloadMarkdown}
              className="flex items-center justify-center space-x-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-4 py-2 h-10 rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-md hover:shadow-lg font-medium min-w-[110px]"
            >
              <Download size={16} />
              <span>Download</span>
            </button>
            <button
              onClick={cancelEdits}
              disabled={editContent === originalMarkdownContent}
              className={`flex items-center justify-center space-x-2 px-4 py-2 h-10 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg font-medium min-w-[80px] ${
                editContent === originalMarkdownContent
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800'
              }`}
              title={editContent === originalMarkdownContent ? 'No changes to reset' : 'Reset to original content'}
            >
              <X size={16} />
              <span>Reset</span>
            </button>
          </div>
        </div>
        
        <div className="flex-1 bg-gray-50 rounded-xl border border-gray-200 flex flex-col" style={{ minHeight: '60vh' }}>
          <div className="flex-1 p-4 overflow-hidden">
            <CodeEditor
              value={editContent}
              onChange={handleEditContentChange}
              placeholder="Enter your markdown content here..."
              originalValue={originalMarkdownContent}
            />
          </div>
          
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-xl">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  {isAutoSaving && (
                    <div className="flex items-center space-x-2 text-blue-600">
                      <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-xs">Auto-saving...</span>
                    </div>
                  )}
                  {showAutoSaveStatus && lastAutoSaved && (
                    <div className="flex items-center space-x-2 text-green-600">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-xs">Auto-saved at {lastAutoSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  )}
                  {hasChanges && (
                    <div className="flex items-center space-x-3 text-gray-600">
                      {totalModified > 0 && (
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                          <span className="text-xs">
                            {totalModified} modified
                          </span>
                        </div>
                      )}
                      {totalAdded > 0 && (
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          <span className="text-xs">
                            {totalAdded} added
                          </span>
                        </div>
                      )}
                      {totalDeleted > 0 && (
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                          <span className="text-xs">
                            {totalDeleted} deleted
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-4 text-xs">
                <span>Lines: {editContent.split('\n').length}</span>
                <span>Characters: {editContent.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Markdown Visualizer and Editor
            </h1>
            <p className="text-gray-600 mt-1">Transform markdown into interactive visualizations</p>
          </div>
          
          <div className="flex items-center space-x-3">
            {markdownContent && (
              <button
                onClick={handleNewCanvas}
                className="flex items-center space-x-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm"
              >
                <FileText size={16} />
                <span>New Canvas</span>
              </button>
            )}
            {markdownContent && (
              <label className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-lg cursor-pointer hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg">
                <Upload size={16} />
                <span>Upload</span>
                <input
                  type="file"
                  accept=".md"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>

        {parsedData && (
          <div className="flex flex-col items-center space-y-3 mb-6">
            <div className="flex space-x-1 bg-white p-1 rounded-xl shadow-md border border-gray-200">
              <button
                onClick={() => setCurrentView('accordion')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 font-medium ${
                  currentView === 'accordion'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <FileText size={16} />
                <span>Accordion</span>
              </button>
              <button
                onClick={() => setCurrentView('flow')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 font-medium ${
                  currentView === 'flow'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <GitBranch size={16} />
                <span>Flow</span>
              </button>
              <button
                onClick={() => setCurrentView('mindmap')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 font-medium ${
                  currentView === 'mindmap'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <Network size={16} />
                <span>Mindmap</span>
              </button>
              <button
                onClick={() => setCurrentView('list')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 font-medium ${
                  currentView === 'list'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <List size={16} />
                <span>List</span>
              </button>
              <button
                onClick={() => {
                  setCurrentView('edit');
                  if (markdownContent) {
                    startEditing();
                  }
                }}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 font-medium ${
                  currentView === 'edit'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <Edit3 size={16} />
                <span>Edit</span>
              </button>
            </div>
            
            {markdownContent && (
              <div className="flex items-center space-x-2 bg-green-50 border border-green-200 px-3 py-2 rounded-lg">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-green-700 font-medium">
                  File uploaded: {fileName} ({Math.round(markdownContent.length / 1024)}KB)
                </span>
              </div>
            )}
          </div>
        )}

        {!parsedData && currentView !== 'edit' ? (
          <div 
            className={`flex items-center justify-center min-h-[60vh] transition-all duration-300 ${
              isDragOver ? 'bg-blue-50' : ''
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="text-center">
              <div className={`w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-all duration-300 ${
                isDragOver 
                  ? 'bg-blue-100 border-2 border-dashed border-blue-400 scale-110' 
                  : 'bg-gradient-to-br from-blue-100 to-purple-100'
              }`}>
                <FileText size={48} className={`transition-colors duration-300 ${
                  isDragOver ? 'text-blue-600' : 'text-blue-600'
                }`} />
              </div>
              
              <h3 className="text-2xl font-semibold text-gray-800 mb-3">
                {isDragOver ? 'Drop your file here' : 'Upload a markdown file to get started'}
              </h3>
              
              <p className="text-gray-600 mb-6">
                {isDragOver ? 'Release to upload' : 'Drag and drop a .md file here, or click to browse'}
              </p>
              
              <div className="flex flex-col items-center space-y-4">
                <div className="flex items-center space-x-3">
                  <label className={`flex items-center space-x-3 px-6 py-3 rounded-xl cursor-pointer transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 ${
                    isDragOver
                      ? 'bg-blue-600 text-white'
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'
                  }`}>
                    <Upload size={20} />
                    <span className="font-medium">Choose File</span>
                    <input
                      type="file"
                      accept=".md"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                  <button
                    onClick={handleNewCanvas}
                    className="flex items-center space-x-3 px-6 py-3 rounded-xl bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-medium"
                  >
                    <FileText size={20} />
                    <span>New Canvas</span>
                  </button>
                </div>
                
                <div className="text-sm text-gray-500 space-y-2">
                  <p>🎯 Visualize markdown content in multiple views</p>
                  <p>✨ Auto-corrects formatting</p>
                  <p>💾 Auto-saves changes</p>
                  <p>📥 Download updated files</p>
                </div>
              </div>
              
              <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg max-w-md mx-auto">
                <p className="text-sm text-blue-800 leading-relaxed">
                  <strong>Perfect for:</strong> Visualizing mindmaps exported as markdown files for user journey flows, 
                  end-to-end test scenarios, project documentation, and hierarchical content organization.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 min-h-[75vh] overflow-hidden">
            <div className="p-6 h-full">
              {currentView === 'accordion' ? renderAccordionView() :
               currentView === 'flow' ? renderFlowView() :
               currentView === 'mindmap' ? <MindmapView parsedData={parsedData!} /> :
               currentView === 'list' ? renderListView() :
               currentView === 'edit' ? renderEditView() : null}
            </div>
          </div>
        )}
      </div>
      
      <footer className="border-t border-gray-200 bg-gradient-to-b from-white to-gray-50 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 inline-block">
              <h3 className="text-base font-semibold text-gray-800 mb-1">Have feedback or found an issue?</h3>
              <p className="text-gray-600 text-sm mb-3">We'd love to hear from you and make this tool even better.</p>
              <a 
                href="mailto:coconadaur@gmail.com" 
                className="inline-flex items-center space-x-2 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-all duration-200 font-medium text-sm"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                </svg>
                <span>Send us an email</span>
              </a>
            </div>

            <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
              <span>Crafted with</span>
              <span className="text-red-500">❤️</span>
              <span>by</span>
              <a 
                href="https://coconadaur.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-semibold text-blue-600 hover:text-blue-800 transition-colors hover:underline"
              >
                coconadaur
              </a>
              <span>•</span>
              <span>Built with help from</span>
              <a 
                href="https://claude.ai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-semibold text-purple-600 hover:text-purple-800 transition-colors hover:underline"
              >
                claude.ai
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MarkdownVisualizer;